"use client";

import { Suspense, useEffect, useState } from "react";
import { BackLink } from "@/components/ui";
import { useSearchParams } from "next/navigation";
import { createClient } from "@/lib/supabase-browser";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";

const SUBJECT_OPTIONS = [
  { value: "General Inquiry", label: "General Inquiry" },
  { value: "Bug Report", label: "Bug Report" },
  { value: "Feature Request", label: "Feature Request" },
  { value: "Billing", label: "Billing" },
  { value: "Other", label: "Other" },
];

export default function ContactPage() {
  return (
    <Suspense>
      <ContactContent />
    </Suspense>
  );
}

function ContactContent() {
  const searchParams = useSearchParams();
  const initialSubject = SUBJECT_OPTIONS.some((o) => o.value === searchParams.get("subject"))
    ? (searchParams.get("subject") as string)
    : "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) return;
      setEmail(data.user.email || "");
      const meta = data.user.user_metadata;
      if (meta?.full_name) setName(meta.full_name);
      else if (meta?.name) setName(meta.name);
    });
  }, []);

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Please enter your name.";
    if (!email.trim()) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "Please enter a valid email address.";
    if (!subject) next.subject = "Please select a subject.";
    if (!message.trim() || message.trim().length < 10) next.message = "Message must be at least 10 characters.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject,
          message,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setServerError(data.error || "Something went wrong.");
        return;
      }
      setSubmitted(true);
    } catch {
      setServerError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <main className="flex-1 px-[var(--space-4)] py-[var(--space-8)]">
        <div className="mx-auto max-w-[var(--container-lg)] text-center space-y-[var(--space-6)] py-[var(--space-16)]">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-[var(--color-success-subtle)]">
            <CheckCircle className="size-8 text-[var(--color-success)]" />
          </div>
          <div className="space-y-[var(--space-2)]">
            <h1 className="font-heading text-[length:var(--font-size-2xl)] tracking-[var(--letter-spacing-tight)] text-[var(--color-text-primary)]">
              Message Sent
            </h1>
            <p className="text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
              Thanks for reaching out! We&apos;ll get back to you as soon as possible.
            </p>
          </div>
          <BackLink href="/">Back to Home</BackLink>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 px-[var(--space-4)] py-[var(--space-8)]">
      <article className="mx-auto max-w-[var(--container-lg)] space-y-[var(--space-8)]">
        <PageHeader
          title="Contact Us"
          description="Have a question or need help? Send us a message."
          breadcrumb={
            <BackLink href="/">Home</BackLink>
          }
        />

        <form onSubmit={handleSubmit} className="space-y-[var(--space-4)]">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name}
            required
          />

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            required
          />

          <Select
            label="Subject"
            options={SUBJECT_OPTIONS}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            error={errors.subject}
            placeholder="Select a subject"
          />

          <Textarea
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            error={errors.message}
            placeholder="Tell us how we can help..."
            rows={5}
            required
          />

          {serverError && (
            <p role="alert" className="text-[length:var(--font-size-sm)] text-[var(--color-error)]">
              {serverError}
            </p>
          )}

          <Button type="submit" loading={loading} size="lg" className="w-full sm:w-auto">
            Send Message
          </Button>
        </form>
      </article>
    </main>
  );
}
